import React from 'react';
import { Instagram, Gift, Calendar, MapPin, Crown, Trophy, Sparkles, Share2, MessageCircle } from 'lucide-react';

// Instagram Story (1080x1920) - Vertical Poster
const InstagramStoryPoster = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div 
        className="relative bg-gradient-to-b from-[#0f1219] via-[#141a24] to-[#0a0d12] overflow-hidden"
        style={{ 
          width: '360px', 
          height: '640px',
          fontFamily: "'Inter', sans-serif"
        }}
      >
        {/* Gold Glow Effects */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[280px] h-[200px] bg-[#FFD700]/12 rounded-full blur-[80px]" />
        
        {/* Confetti Decorations */}
        <div className="absolute top-12 left-6 w-2 h-2 bg-[#FFD700] rotate-45" />
        <div className="absolute top-24 right-10 w-2.5 h-2.5 bg-[#8B9A46] rounded-full" />
        <div className="absolute top-40 left-12 w-1.5 h-1.5 bg-[#FFD700]/60 rounded-full" />
        <div className="absolute bottom-40 left-8 w-2 h-2 bg-[#A4B55C] rotate-12" />
        <div className="absolute bottom-28 right-8 w-1.5 h-1.5 bg-[#FFD700] rounded-full" />
        <div className="absolute top-[180px] right-16 w-1.5 h-1.5 bg-[#FFD700]/40" />
        <div className="absolute bottom-[160px] left-16 w-2.5 h-2.5 bg-[#8B9A46]/60 rotate-45" />
        <div className="absolute top-[260px] left-4 w-1 h-1 bg-[#FFD700]" />
        <div className="absolute bottom-[200px] right-12 w-2 h-2 bg-[#A4B55C]/40 rounded-full" />
        
        {/* Content Container */}
        <div className="relative z-10 h-full flex flex-col items-center justify-between p-6">
          
          {/* Header - Logo */}
          <div className="flex items-center justify-center w-full pt-4">
            <img 
              src="https://www.kingofit.ca/images/logo.png" 
              alt="King of IT"
              className="h-8"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center gap-1.5">
              <Crown className="w-5 h-5 text-[#FFD700]" />
              <span className="font-bold text-lg text-white tracking-wider">KING<span className="text-[#FFD700]">of</span>IT</span>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            {/* Giveaway Badge */}
            <div className="flex items-center gap-2 bg-[#FFD700]/20 px-4 py-1.5 rounded-full border border-[#FFD700]/40 mb-4">
              <Sparkles className="w-4 h-4 text-[#FFD700]" />
              <span className="text-[#FFD700] font-semibold text-xs tracking-wide">EXCLUSIVE GIVEAWAY</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="font-black text-4xl text-white leading-none tracking-tight mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              YOU COULD
            </h1>
            <h1 className="font-black text-6xl leading-none tracking-tight mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", background: 'linear-gradient(135deg, #FFD700, #FFA500)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              WIN BIG!
            </h1>
            
            {/* Prize Card */}
            <div className="relative bg-gradient-to-br from-[#1a2030] to-[#0f141c] rounded-2xl p-5 border border-[#FFD700]/20 shadow-xl mb-5 w-[220px]">
              {/* Trophy Icon */}
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#FFA500] rounded-full flex items-center justify-center shadow-lg">
                <Trophy className="w-5 h-5 text-[#0f1219]" />
              </div>
              
              <div className="pt-4">
                <p className="text-[#8B9A46] text-[10px] font-medium mb-1">GRAND PRIZE</p>
                <p className="text-5xl font-black text-[#FFD700] leading-none mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>$100</p>
                <p className="text-white text-xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>Gift Card</p>
                
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-gray-400 text-[8px] mb-1">Redeemable at</p>
                  <div className="flex items-center justify-center gap-1.5">
                    <div className="w-5 h-5 bg-[#8B9A46]/30 rounded-full flex items-center justify-center">
                      <Gift className="w-2.5 h-2.5 text-[#A4B55C]" />
                    </div>
                    <span className="text-white text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>Olive Branch</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-1 text-gray-500 text-[8px]">
                    <MapPin className="w-2.5 h-2.5" />
                    <span>Promenade Mall, Thornhill</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Draw Date */}
            <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10 mb-4">
              <Calendar className="w-4 h-4 text-[#FFD700]" />
              <span className="text-white text-sm">Draw: <span className="text-[#FFD700] font-semibold">Jan 25, 2026</span></span>
            </div>
          </div>
          
          {/* Footer - How to Enter */}
          <div className="w-full pb-4">
            <p className="text-gray-400 text-center text-[10px] mb-3 tracking-widest">HOW TO ENTER</p>
            <div className="flex flex-col items-center gap-2 mb-4">
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10 w-[180px] justify-center">
                <div className="w-5 h-5 bg-[#FFD700]/20 rounded-full flex items-center justify-center">
                  <span className="text-[#FFD700] font-bold text-[10px]">1</span>
                </div>
                <Instagram className="w-3 h-3 text-[#FFD700]" />
                <span className="text-white text-xs">Follow</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10 w-[180px] justify-center">
                <div className="w-5 h-5 bg-[#FFD700]/20 rounded-full flex items-center justify-center">
                  <span className="text-[#FFD700] font-bold text-[10px]">2</span>
                </div>
                <Share2 className="w-3 h-3 text-[#FFD700]" />
                <span className="text-white text-xs">Share</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10 w-[180px] justify-center">
                <div className="w-5 h-5 bg-[#FFD700]/20 rounded-full flex items-center justify-center">
                  <span className="text-[#FFD700] font-bold text-[10px]">3</span>
                </div>
                <MessageCircle className="w-3 h-3 text-[#FFD700]" />
                <span className="text-white text-xs">DM Us</span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-[#FFD700] font-bold text-lg">@kingofit.ca</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstagramStoryPoster;
